import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  ContactUsTypes,
  IBreadcrumbData,
  IContactUsBreadcrumbData,
  IEquisBreadcrumbTree,
} from 'src/app/shared/models/breadcrumb';
import { helpCenterNode, homeNode } from './breadcrumbFactory';

@Injectable({
  providedIn: 'root',
})
export class BreadcrumbService {
  initialBreadcrumbs: IEquisBreadcrumbTree[] = [homeNode, helpCenterNode];
  private currentBreadcrumbs: IEquisBreadcrumbTree[] = [];
  private readonly STORAGE_KEY = 'BREADCRUMB_STATE';

  breadcrumbData = new BehaviorSubject<
    Array<IEquisBreadcrumbTree | IContactUsBreadcrumbData>
  >(this.initialBreadcrumbs);

  constructor(private readonly router: Router) {
    const stored = this.loadFromStorage();
    if (stored) {
      this.currentBreadcrumbs = stored;
      this.breadcrumbData.next(stored);
    }
  }

  public setBreadcrumb(data: IBreadcrumbData): void {
    if (!data) return;

    const newBreadcrumbs = this.resolveNewBreadcrumbs(data);

    const finalBreadcrumbs = this.normalizeAndSyncBreadcrumbs(newBreadcrumbs);

    this.breadcrumbData.next(finalBreadcrumbs);
  }

  private resolveNewBreadcrumbs(
    data: IBreadcrumbData,
  ): Array<IEquisBreadcrumbTree | IContactUsBreadcrumbData> {
    if (data.preloaded) {
      return data.preloaded;
    }

    if (Object.values(ContactUsTypes).includes(data.id as ContactUsTypes)) {
      return this.buildContactUsBreadcrumbs(data.id);
    }

    return this.getStoredOrFallback();
  }

  public resetBreadcrumb(): void {
    this.currentBreadcrumbs = [];
    sessionStorage.removeItem(this.STORAGE_KEY);
    this.breadcrumbData.next([]);
  }

  public getCurrentBreadcrumbs(): IEquisBreadcrumbTree[] {
    return this.currentBreadcrumbs;
  }

  public getBreadcrumbsForNextStep(): IEquisBreadcrumbTree[] {
    const trails = [...this.currentBreadcrumbs];
    if (trails.length > 0) {
      trails[trails.length - 1].active = false;
    }
    return trails;
  }

  public getBreadcrumb(): Observable<
    Array<IEquisBreadcrumbTree | IContactUsBreadcrumbData>
  > {
    return this.breadcrumbData.asObservable();
  }

  private normalizeAndSyncBreadcrumbs(
    breadcrumbs: Array<IEquisBreadcrumbTree | IContactUsBreadcrumbData>,
  ): Array<IEquisBreadcrumbTree | IContactUsBreadcrumbData> {
    let finalBreadcrumbs = breadcrumbs;

    if (
      breadcrumbs.length > 0 &&
      (breadcrumbs[0] as IEquisBreadcrumbTree).title !==
        this.initialBreadcrumbs[0].title
    ) {
      finalBreadcrumbs = [...this.initialBreadcrumbs, ...breadcrumbs];
    }

    this.currentBreadcrumbs = finalBreadcrumbs as IEquisBreadcrumbTree[];
    this.saveToStorage(this.currentBreadcrumbs);

    return finalBreadcrumbs;
  }

  private getStoredOrFallback(): IEquisBreadcrumbTree[] {
    const stored = this.loadFromStorage();
    if (stored) {
      return stored;
    }
    return [...this.initialBreadcrumbs];
  }

  private buildContactUsBreadcrumbs(id: string): IEquisBreadcrumbTree[] {
    const contactChannelsBreadcrumb = {
      path: '/contacto',
      title: 'Canales de atención',
    } as any;

    const phonesBreadcrumb = {
      path: '/telefonos',
      title: 'Teléfonos',
    } as any;

    if (id === ContactUsTypes.CONTACT_CHANNELS) {
      return [contactChannelsBreadcrumb];
    }
    return [contactChannelsBreadcrumb, phonesBreadcrumb];
  }

  private saveToStorage(breadcrumbs: IEquisBreadcrumbTree[]): void {
    try {
      const state = {
        url: this.router.url,
        breadcrumbs,
      };
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      console.warn('Failed to save breadcrumbs to sessionStorage', e);
    }
  }

  private loadFromStorage(): IEquisBreadcrumbTree[] | null {
    try {
      const raw = sessionStorage.getItem(this.STORAGE_KEY);
      if (!raw) return null;

      const state = JSON.parse(raw);
      if (state.url === this.router.url) {
        return state.breadcrumbs;
      }
      return null;
    } catch (e) {
      console.warn('Failed to load breadcrumbs from sessionStorage', e);
      return null;
    }
  }
}
